import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type PdfWatermarkSettings,
	type PdfWatermarkSettingsRequest,
	type TenantConfigPlane,
	ValidationErrorCode,
} from '@today/types'
import { isAxiosError } from 'axios'
import { pdfSettingsService } from '@/services/pdf-settings.service'

/** Why a save was refused, in the terms the section shows it (TO-495 Contract errors). */
export type PdfWatermarkSaveError = { kind: 'invalid'; field: string; token?: string } | { kind: 'failed' }

interface ApiErrorBody {
	error?: { code?: string; details?: { field?: string; token?: string } }
}

/** Maps a save failure to its kind: a field rule (with the refused token), or anything else. */
export const watermarkSaveErrorOf = (error: unknown): PdfWatermarkSaveError => {
	const body = isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error : undefined
	if (body?.code === ValidationErrorCode.PDF_WATERMARK_INVALID) {
		const field = body.details?.field ?? ''
		const token = body.details?.token
		return token === undefined ? { kind: 'invalid', field } : { kind: 'invalid', field, token }
	}
	return { kind: 'failed' }
}

/**
 * A tenant's offer-PDF watermark and its save (TO-495 REQ-011), on the route
 * of the console's plane. A stored save refetches the settings.
 */
export function usePdfWatermarkSettings(tenantId: string, tenantSlug: string, plane: TenantConfigPlane) {
	const client = useQueryClient()
	const queryKey = ['pdf-watermark-settings', plane, tenantId] as const
	const query = useQuery<PdfWatermarkSettings>({
		queryKey,
		queryFn: () => pdfSettingsService.getWatermark(tenantId, tenantSlug, plane),
	})
	const saveMutation = useMutation({
		mutationFn: (input: PdfWatermarkSettingsRequest) =>
			pdfSettingsService.saveWatermark(tenantId, tenantSlug, plane, input),
		onSuccess: () => client.invalidateQueries({ queryKey }),
	})
	return {
		settings: query.data ?? null,
		loadFailed: query.isError,
		reload: () => void query.refetch(),
		save: (input: PdfWatermarkSettingsRequest) => saveMutation.mutate(input),
		saving: saveMutation.isPending,
		saveError: saveMutation.isError ? watermarkSaveErrorOf(saveMutation.error) : null,
	}
}
